import React, { useState, useRef } from 'react';
import { exportToJSON, exportToCSV, exportBackup, parseJSONDeck, parseCSVDeck, parseBackup } from '../utils/exportUtils';
import './ImportExport.css';

export const ImportExport = ({
  allDecks,
  customDecks,
  memorized,
  favorites,
  theme,
  reviewData,
  onImportDeck,
  onRestoreBackup,
  onClose
}) => {
  const [selectedDeck, setSelectedDeck] = useState('');
  const [importType, setImportType] = useState('deck'); // 'deck' or 'backup'
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef(null);

  const handleExportJSON = () => {
    if (!selectedDeck) {
      setError('Please select a deck to export');
      return;
    }

    const cards = allDecks[selectedDeck];
    exportToJSON(selectedDeck, cards);
    setSuccess(`Exported "${selectedDeck}" as JSON`);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleExportCSV = () => {
    if (!selectedDeck) {
      setError('Please select a deck to export');
      return;
    }

    const cards = allDecks[selectedDeck];
    exportToCSV(selectedDeck, cards);
    setSuccess(`Exported "${selectedDeck}" as CSV`);
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleExportBackup = () => {
    exportBackup(customDecks, memorized, theme, reviewData, favorites);
    setSuccess('Full backup exported successfully');
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setError('');
    setSuccess('');

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target.result;

        if (importType === 'backup') {
          const backup = parseBackup(content);
          if (window.confirm('This will restore all data. Continue?')) {
            onRestoreBackup(backup);
            setSuccess('Backup restored successfully!');
            setTimeout(() => {
              setSuccess('');
              onClose();
            }, 2000);
          }
        } else {
          // Import deck
          let deck;

          if (file.name.endsWith('.json')) {
            deck = parseJSONDeck(content);
          } else if (file.name.endsWith('.csv')) {
            const deckName = file.name.replace('.csv', '').replace(/_/g, ' ');
            deck = parseCSVDeck(content, deckName);
          } else {
            throw new Error('Unsupported file format. Use JSON or CSV.');
          }

          onImportDeck(deck.name, deck.cards);
          setSuccess(`Imported deck "${deck.name}" with ${deck.cards.length} cards`);
          setTimeout(() => setSuccess(''), 3000);
        }
      } catch (err) {
        setError(err.message);
      }
    };

    reader.onerror = () => {
      setError('Failed to read file');
    };

    reader.readAsText(file);

    // Reset file input
    event.target.value = '';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="import-export" onClick={(e) => e.stopPropagation()}>
        <div className="import-export-header">
          <h2>Import / Export</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="import-export-content">
          {/* Export Section */}
          <section className="ie-section">
            <h3>Export Deck</h3>
            <p className="ie-description">
              Export a single deck to JSON or CSV format
            </p>

            <div className="form-group">
              <label>Select Deck</label>
              <select
                className="ie-select"
                value={selectedDeck}
                onChange={(e) => {
                  setSelectedDeck(e.target.value);
                  setError('');
                }}
              >
                <option value="">Choose a deck...</option>
                {Object.keys(allDecks).map(deckName => (
                  <option key={deckName} value={deckName}>
                    {deckName} ({allDecks[deckName].length} cards)
                  </option>
                ))}
              </select>
            </div>

            <div className="button-group">
              <button
                className="btn-export"
                onClick={handleExportJSON}
                disabled={!selectedDeck}
              >
                📄 Export as JSON
              </button>
              <button
                className="btn-export"
                onClick={handleExportCSV}
                disabled={!selectedDeck}
              >
                📊 Export as CSV
              </button>
            </div>
          </section>

          <div className="ie-divider"></div>

          {/* Import Section */}
          <section className="ie-section">
            <h3>Import Deck</h3>
            <p className="ie-description">
              Import a deck from JSON or CSV file
            </p>

            <input
              ref={fileInputRef}
              type="file"
              accept=".json,.csv"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />

            <button
              className="btn-import"
              onClick={() => {
                setImportType('deck');
                fileInputRef.current.click();
              }}
            >
              📥 Import Deck
            </button>

            <div className="ie-hint">
              Supported formats: JSON, CSV
            </div>

            <div className="format-examples">
              <details>
                <summary>View Format Examples</summary>
                <div className="format-content">
                  <h4>JSON Format:</h4>
                  <pre>{`{
  "name": "My Deck",
  "cards": [
    {
      "ar": "مرحبا",
      "en": "Hello",
      "tags": ["greetings", "basic"]
    },
    {
      "ar": "شكرا",
      "en": "Thank you"
    }
  ]
}`}</pre>
                  <a href="/example-deck.json" download className="download-example">
                    📥 Download JSON Example
                  </a>

                  <h4>CSV Format:</h4>
                  <pre>{`Arabic,English
"مرحبا","Hello"
"شكرا","Thank you"
"كتاب","Book"`}</pre>
                  <a href="/example-deck.csv" download className="download-example">
                    📥 Download CSV Example
                  </a>

                  <p className="format-note">
                    <strong>Note:</strong> Tags are only supported in JSON format. CSV imports will not include tags.
                  </p>
                </div>
              </details>
            </div>
          </section>

          <div className="ie-divider"></div>

          {/* Backup Section */}
          <section className="ie-section">
            <h3>Backup & Restore</h3>
            <p className="ie-description">
              Export all data or restore from a backup file
            </p>

            <div className="button-group">
              <button
                className="btn-backup"
                onClick={handleExportBackup}
              >
                💾 Create Backup
              </button>
              <button
                className="btn-restore"
                onClick={() => {
                  setImportType('backup');
                  fileInputRef.current.click();
                }}
              >
                📂 Restore Backup
              </button>
            </div>

            <div className="ie-hint">
              Backup includes all custom decks, progress, and settings
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};
